import { useState } from "react";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import InfoTooltip from "@/components/InfoTooltip";
import ChartFrame from "@/components/ChartFrame";
import { endpointDot } from "@/lib/chartStyle";
import { fmt, toolLabel } from "@/lib/format";
import { ComposedChart, Area, Line, Legend, BarChart, Bar, LabelList, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Panel from "../Panel";
import { Collapse } from "./lib";

// ── 경영 시나리오 세그먼트 (2026-07-20) — 경영 보고의 두괄식 스토리텔링.
// 흐름: ① 결론(순이익 시뮬레이터) → ② 근거(메시지 1건의 해부 = 공식) →
// ③ 구조적 이점(캐싱 턴 곡선) → ④ 사용자 언어 환산(만원의 가치).
// 시뮬레이터는 실측 평균(건당 원가·토큰 해부)을 초기값으로 쓰는 순수 클라이언트 계산.
// 요율 상수는 기본 모델(울트라, $5/$25) 기준 — 해부·턴 곡선에 명시.
const ULTRA = { inKrw: 7.5, outKrw: 37.5, cwKrw: 9.375, crKrw: 0.75 }; // ₩/1k토큰 (원가)
const TOSS_FEE = 0.033;
const INFRA_PCT = 0.02; // Lambda·DDB·S3·CF — 현 규모 실측 근사(매출 대비)

function ScenarioSegment({ d }) {
  const t = d.totals || {};
  const [users, setUsers] = useState(100);
  const [payKrw, setPayKrw] = useState(10000);
  const [burnPct, setBurnPct] = useState(80);
  const [buffer, setBuffer] = useState(4.0);
  const [promo, setPromo] = useState(() => d.promoX2 !== false);

  // ── ① 결론: 순이익 시뮬레이션 ──
  const promoMult = promo ? 2 : 1;
  const revenue = users * payKrw;                                  // 월 매출(실결제)
  const creditsGiven = revenue * promoMult;                        // 지급 크레딧
  const creditsBurned = creditsGiven * (burnPct / 100);            // 소진 크레딧
  const modelCost = creditsBurned / buffer;                        // 모델 원가(차감=원가×버퍼의 역산)
  const fee = revenue * TOSS_FEE;
  const infra = revenue * INFRA_PCT;
  const profit = revenue - modelCost - fee - infra;
  const marginPct = revenue ? (profit / revenue) * 100 : 0;
  const cumData = Array.from({ length: 12 }, (_, i) => ({ m: `${i + 1}월차`, "누적": Math.round(profit * (i + 1)) }));

  // ── ② 근거: 실측 평균 메시지 1건의 해부 ──
  const ta = t.tokAvg;
  const anatomy = ta ? [
    { name: "출력 (답변 생성)", tokens: ta.output, krw: (ta.output / 1000) * ULTRA.outKrw, color: "#f43f5e" },
    { name: "캐시 쓰기 (프롬프트 등록)", tokens: ta.cacheWrite, krw: (ta.cacheWrite / 1000) * ULTRA.cwKrw, color: "#f59e0b" },
    { name: "입력 (본문+지시)", tokens: ta.input, krw: (ta.input / 1000) * ULTRA.inKrw, color: "#0ea5e9" },
    { name: "캐시 읽기 (재사용, 90% 할인)", tokens: ta.cacheRead, krw: (ta.cacheRead / 1000) * ULTRA.crKrw, color: "#10b981" },
  ] : [];
  const anatomyTotal = anatomy.reduce((a, x) => a + x.krw, 0);

  // ── ③ 캐싱 턴 곡선 (실측 평균 토큰 기반 모델링) ──
  const turnData = [];
  if (ta) {
    const prefix = Math.max(ta.cacheWrite, 1);   // 시스템+지침+KB 프리픽스
    for (let turn = 1; turn <= 8; turn++) {
      const noCache = ((ta.input + prefix) / 1000) * ULTRA.inKrw + (ta.output / 1000) * ULTRA.outKrw;
      const withCache = turn === 1
        ? (ta.input / 1000) * ULTRA.inKrw + (prefix / 1000) * ULTRA.cwKrw + (ta.output / 1000) * ULTRA.outKrw
        : (ta.input / 1000) * ULTRA.inKrw + (prefix / 1000) * ULTRA.crKrw + (ta.output / 1000) * ULTRA.outKrw;
      turnData.push({ turn: `${turn}턴`, "캐싱 없음": Math.round(noCache), "캐싱 적용": Math.round(withCache) });
    }
  }
  const turnSaving = turnData.length
    ? Math.round((1 - turnData.reduce((a, x) => a + x["캐싱 적용"], 0) / turnData.reduce((a, x) => a + x["캐싱 없음"], 0)) * 100)
    : 0;

  // ── ④ 만원의 가치: 결제액으로 도구별 몇 번 쓰나 ──
  const perToolUses = (d.toolMultiples || [])
    .filter((tm) => tm.n >= 3 && tm.deductedNc > 0)
    .map((tm) => ({ tool: toolLabel(tm.tool).replace(/\s*\(.*?\)/g, "").trim() || toolLabel(tm.tool),
                    per: tm.deductedNc / tm.n,
                    uses: Math.floor((payKrw * promoMult) / (tm.deductedNc / tm.n)) }))
    .sort((a, b) => b.uses - a.uses).slice(0, 8);
  const maxUses = Math.max(1, ...perToolUses.map((x) => x.uses));

  const sliderCls = "w-full accent-brand-600";
  const numCls = "w-24 rounded-lg border border-ink-200 px-2 py-1 text-right text-[13px] tabular-nums focus:border-brand-400 focus:outline-none";

  return (
    <>
      {/* 01 결론 — 2026-07-20 재조판: 한 패널에 몰렸던 결론+슬라이더+차트+표를
          3개 패널(결론 / 가정 바꿔보기 / 12개월 곡선)로 풀어 호흡 확보. */}
      <Panel title="01 · 결론 — 이 조건이면 얼마가 남는가"
             action={<InfoTooltip text="실측 구조(차감 = 원가 × 마진 배수, 토스 수수료 3.3%, 인프라 ≈ 매출 2%) 기반 계산. 소진율 = 지급 크레딧 중 실제 사용 비율 — 안 쓰고 남는 크레딧은 전부 이익." />}>
        <p className="text-[15px] leading-snug text-ink-500">
          유료 <b className="text-ink-900">{fmt.int(users)}명</b>이 월 <b className="text-ink-900">{fmt.krw(payKrw)}</b>씩 결제하면
        </p>
        <p className="mt-1.5 text-4xl font-bold tabular-nums tracking-tight text-brand-700 sm:text-5xl">
          월 기여이익 약 {fmt.krw(Math.round(profit))}
        </p>
        <p className="mt-1.5 text-[14px] font-medium text-ink-500">
          마진율 {marginPct.toFixed(0)}% · 매출 ÷ 지출 {(revenue / Math.max(1, modelCost + fee + infra)).toFixed(1)}배
        </p>

        <div className="mt-7">
          <p className="mb-2 text-[12.5px] font-medium text-ink-500">매출 {fmt.krw(revenue)}의 행방</p>
          <div className="flex h-10 w-full overflow-hidden rounded-xl">
            <div style={{ width: `${(modelCost / revenue) * 100}%` }} className="bg-rose-400" />
            <div style={{ width: `${(fee / revenue) * 100}%` }} className="bg-amber-400" />
            <div style={{ width: `${(infra / revenue) * 100}%` }} className="bg-slate-300" />
            <div style={{ width: `${Math.max(0, (profit / revenue) * 100)}%` }} className="bg-emerald-400" />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-10 gap-y-2.5">
            {[["모델원가", modelCost, "bg-rose-400"], ["결제 수수료", fee, "bg-amber-400"],
              ["인프라", infra, "bg-slate-300"], ["기여이익", profit, "bg-emerald-400"]].map(([nm, v, c]) => (
              <div key={nm} className="text-[13px]">
                <span className="flex items-center gap-1.5 text-ink-500">
                  <span className={`h-2.5 w-2.5 rounded-sm ${c}`} />{nm}
                </span>
                <span className="mt-0.5 block pl-4 font-semibold tabular-nums text-ink-800">
                  {fmt.krw(Math.round(v))}
                  <span className="ml-1 font-normal text-ink-400">({((v / revenue) * 100).toFixed(0)}%)</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-7 overflow-x-auto">
          <table className="w-full min-w-[420px] text-[13px]">
            <thead><tr className="text-left text-ink-400">
              <th className="pb-2 font-medium">기간</th>
              <th className="pb-2 text-right font-medium">매출</th>
              <th className="pb-2 text-right font-medium">총지출</th>
              <th className="pb-2 text-right font-medium">기여이익</th>
            </tr></thead>
            <tbody>
              {[["월", 1], ["분기", 3], ["연", 12]].map(([label, m]) => (
                <tr key={label} className="border-t border-ink-100">
                  <td className="py-2.5">{label}</td>
                  <td className="py-2.5 text-right tabular-nums">{fmt.krw(revenue * m)}</td>
                  <td className="py-2.5 text-right tabular-nums">{fmt.krw(Math.round((modelCost + fee + infra) * m))}</td>
                  <td className="py-2.5 text-right font-semibold tabular-nums text-emerald-700">{fmt.krw(Math.round(profit * m))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="가정 바꿔보기"
             action={<span className="text-[12px] text-ink-400">움직이면 위 결론이 실시간으로 따라옵니다</span>}>
        <div className="grid gap-x-10 gap-y-7 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "유료 사용자", val: `${fmt.int(users)}명`, min: 10, max: 3000, step: 10, v: users, set: setUsers, lo: "10명", hi: "3,000명" },
            { label: "인당 월 결제", val: fmt.krw(payKrw), min: 5000, max: 100000, step: 5000, v: payKrw, set: setPayKrw, lo: "₩5,000", hi: "₩100,000" },
            { label: "크레딧 소진율", val: `${burnPct}%`, min: 30, max: 100, step: 5, v: burnPct, set: setBurnPct, lo: "30% (많이 남김)", hi: "100% (전량 사용)" },
            { label: "마진 배수 — 원가의 몇 배로 파는가", val: `${buffer.toFixed(1)}×`, min: 2, max: 6, step: 0.5, v: buffer, set: setBuffer, lo: "2.0×", hi: "6.0× (현행 4.0)" },
          ].map((sl) => (
            <label key={sl.label} className="block">
              <span className="flex items-baseline justify-between text-[13px] text-ink-500">
                {sl.label}
                <b className="text-[15px] tabular-nums text-ink-900">{sl.val}</b>
              </span>
              <input type="range" min={sl.min} max={sl.max} step={sl.step} value={sl.v}
                     onChange={(e) => sl.set(+e.target.value)}
                     className="mt-2.5 h-1.5 w-full accent-brand-600" />
              <span className="mt-1 flex justify-between text-[11px] text-ink-300"><span>{sl.lo}</span><span>{sl.hi}</span></span>
            </label>
          ))}
        </div>
        <label className="mt-6 flex items-center justify-between rounded-xl bg-ink-50/70 px-3.5 py-2.5 text-[13px]">
          <span className="text-ink-600">충전 ×2 프로모 반영 <span className="text-ink-400">(2026-09-09 종료 — 끄면 프로모 이후 시나리오)</span></span>
          <input type="checkbox" checked={promo} onChange={(e) => setPromo(e.target.checked)} className="h-4 w-4 accent-brand-600" />
        </label>
      </Panel>

      <Panel title="12개월 누적 기여이익 (현 조건 유지 가정)">
        <ChartFrame base="h-52 sm:h-60" tall="h-80 sm:h-[28rem]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={cumData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f2f2f0" />
              <XAxis dataKey="m" tick={{ fill: "#8e8e8a", fontSize: 11 }} axisLine={false} tickLine={false} tickMargin={8} />
              <YAxis tick={{ fill: "#8e8e8a", fontSize: 11 }} tickFormatter={fmt.compact} width={52} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 14, border: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.14)", fontSize: 12, padding: "8px 12px" }}
                       formatter={(v) => [fmt.krw(v), "누적 기여이익"]} />
              <Area type="natural" dataKey="누적" stroke="#2563eb" strokeWidth={3} fill="url(#profitGrad)"
                    dot={false} activeDot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartFrame>
      </Panel>

      {/* ② 근거 — 메시지 1건의 해부 */}
      <Collapse title="02 · 근거 — 메시지 1건을 보낼 때 무슨 일이 일어나는가"
             action={<span className="text-[12px] text-ink-400">이 기간 실측 평균 토큰 · 요율은 기본 모델(울트라 $5/$25) 기준</span>}>
        {ta ? (
          <>
            <div className="mb-2 h-14">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={[Object.fromEntries([["name", "평균 1건"], ...anatomy.map((a) => [a.name, Math.round(a.krw * 10) / 10])])]}
                          margin={{ top: 0, right: 12, left: 0, bottom: 0 }} barSize={26}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" hide />
                  <Tooltip contentStyle={{ borderRadius: 14, border: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.14)", fontSize: 12, padding: "8px 12px" }}
                           formatter={(v, name) => [fmt.krw(v), name]} />
                  {anatomy.map((a, i) => (
                    <Bar key={a.name} dataKey={a.name} stackId="c" fill={a.color}
                         radius={i === 0 ? [6, 0, 0, 6] : i === anatomy.length - 1 ? [0, 6, 6, 0] : 0} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-10 gap-y-3">
              {anatomy.map((a) => (
                <div key={a.name} className="text-[13px]">
                  <span className="flex items-center gap-1.5 text-ink-500">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: a.color }} />
                    {a.name}
                  </span>
                  <span className="mt-0.5 block pl-4 font-semibold tabular-nums text-ink-800">
                    {fmt.krw(Math.round(a.krw))}
                    <span className="ml-1 font-normal text-ink-400">({anatomyTotal ? Math.round((a.krw / anatomyTotal) * 100) : 0}%)</span>
                  </span>
                  <span className="block pl-4 text-[12px] tabular-nums text-ink-400">{fmt.compact(a.tokens)}토큰</span>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-xl bg-ink-50/70 px-3.5 py-3 font-mono text-[12px] leading-relaxed text-ink-700">
              평균 1건 원가 ≈ {fmt.krw(Math.round(anatomyTotal))} → 차감 = 원가 × 마진 배수 4.0 ≈ {fmt.nc(Math.round(anatomyTotal * 4))}크레딧<br />
              (입력·출력 토큰이 매번 실측되어 이 공식으로 자동 차감 — 비싼 요청은 비싸게, 싼 요청은 싸게)
            </div>
          </>
        ) : <p className="text-sm text-ink-400">이 기간 토큰 데이터가 없습니다.</p>}
      </Collapse>

      {/* ③ 구조적 이점 — 캐싱 턴 곡선 */}
      <Collapse title="03 · 구조적 이점 — 대화가 길어질수록 싸진다 (프롬프트 캐싱)"
             action={<span className="text-[12px] text-ink-400">실측 평균 토큰 기반 모델링 · 8턴 대화 시 누적 원가 {turnSaving}% 절감</span>}>
        <ChartFrame base="h-44 sm:h-52" tall="h-72 sm:h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={turnData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cacheGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f2f2f0" />
              <XAxis dataKey="turn" tick={{ fill: "#8e8e8a", fontSize: 11 }} axisLine={false} tickLine={false} tickMargin={8} />
              <YAxis tick={{ fill: "#8e8e8a", fontSize: 11 }} tickFormatter={fmt.compact} width={44} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 14, border: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.14)", fontSize: 12, padding: "8px 12px" }}
                       formatter={(v, name) => [fmt.krw(v), name]} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Line type="natural" dataKey="캐싱 없음" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 4" dot={false} />
              <Area type="natural" dataKey="캐싱 적용" stroke="#10b981" strokeWidth={3} fill="url(#cacheGrad)"
                    dot={endpointDot("#10b981", turnData.length)} activeDot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartFrame>
        <p className="mt-2 text-[12px] text-ink-400">
          1턴: 프롬프트를 캐시에 등록(등록비 1.25배)해 잠깐 비싸지만, 2턴부터 같은 프리픽스를 1/10 가격에 읽는다 —
          대화형 서비스일수록 원가 구조가 유리해지는 이유.
        </p>
      </Collapse>

      {/* ④ 사용자 언어 환산 — 만원의 가치 */}
      <Collapse title={`04 · 환산 — ${fmt.krw(payKrw)} 결제하면 몇 번 쓸 수 있나`}
             action={<span className="text-[12px] text-ink-400">{promo ? `프로모 ×2 → ${fmt.nc(payKrw * 2)}크레딧` : `${fmt.nc(payKrw)}크레딧`} 기준 · 이 기간 도구별 평균 차감으로 계산</span>}>
        {perToolUses.length ? (
          <>
            <div style={{ height: perToolUses.length * 34 + 8 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={perToolUses} margin={{ top: 0, right: 56, left: 8, bottom: 0 }} barSize={18}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="tool" width={128}
                         tick={{ fill: "#57534e", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 14, border: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.14)", fontSize: 12, padding: "8px 12px" }}
                           formatter={(v, name, p) => [`약 ${fmt.int(v)}회 (건당 ${fmt.nc(Math.round(p.payload.per))}크레딧)`, "사용 가능"]} />
                  <Bar dataKey="uses" fill="#2563eb" fillOpacity={0.75} radius={[0, 6, 6, 0]}>
                    <LabelList dataKey="uses" position="right" formatter={(v) => `약 ${fmt.int(v)}회`}
                               style={{ fill: "#44403c", fontSize: 12, fontVariantNumeric: "tabular-nums" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="pt-1 text-[12px] text-ink-400">건당 평균 차감(기간 실측) 기준 — 긴 기사·상위 모델을 쓰면 줄어들 수 있습니다.</p>
          </>
        ) : <p className="text-sm text-ink-400">표본이 적어(도구당 3건 미만) 환산을 생략했습니다.</p>}
      </Collapse>
    </>
  );
}

// ── 정책·규칙 세그먼트 v2 (2026-07-20) — 가격 정책의 단일 참조점을 "읽는 문서"가
// 아니라 "보는 화면"으로. 구성: ① 핵심 정책 카드 ② 공식+요율표(원가·판매 병기 =
// 마진 구조가 표에서 보임) ③ 크레딧·이미지 정액 ④ 감시 임계값 ⑤ 변경 이력
// ⑥ 체크리스트. 모든 수치의 소스는 코드(credit_hook 등) — 개정 시 함께 갱신.

export default ScenarioSegment;
